/**
 * Product fork — shown when an event offers BOTH registration and
 * sponsorships. Single-product events auto-redirect to the one they
 * support, so this screen only renders when there's a real choice.
 *
 * Copy is unit-neutral: the register CTA is just "Register" for charity
 * events (the organizer's division options name the actual unit —
 * foursome, individual, table, …) and "Register a team" for club events.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import type { ConfigResponse, EmbedClient } from '@/api/embed';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import { useVocab } from '@/state/vocab';
import '@/pages/ChooseProductPage.css';

interface Props {
  client: EmbedClient;
  config: ConfigResponse | null;
}

export function GfgChooseProductPage({ client, config }: Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useFlowNavigate();

  const vocab = useVocab();
  const event = config?.allowed_events.find((e) => e.id === eventId) ?? null;
  const hasReg = event?.has_registration ?? false;
  const hasSpon = event?.has_sponsorships ?? false;

  // Most people pick Register: load its division screen while they read.
  useEffect(() => {
    if (eventId && hasReg) client.prefetchEvent(eventId);
  }, [client, eventId, hasReg]);

  // Single-product events skip the fork entirely.
  useEffect(() => {
    if (!event) return;
    if (hasReg && !hasSpon) {
      navigate(`/events/${eventId}/register`, { replace: true });
    } else if (hasSpon && !hasReg) {
      navigate(`/events/${eventId}/sponsor`, { replace: true });
    }
  }, [event, hasReg, hasSpon, eventId, navigate]);

  if (!config) {
    return <div className="tn-choose__loading">Loading…</div>;
  }

  if (!event) {
    return (
      <div role="alert" className="tn-choose__error">
        This event is not available through this link.
      </div>
    );
  }

  if (!hasReg && !hasSpon) {
    return (
      <div className="tn-card tn-choose__empty">
        <h2>Not open yet</h2>
        <p>The organizer hasn't opened registration or sponsorships for this event yet.</p>
      </div>
    );
  }

  // A single-product event is mid-redirect (effect above) — render nothing.
  if (hasReg !== hasSpon) return null;

  return (
    <div className="tn-choose">
      <h1 className="tn-choose__title">{event.name}</h1>
      <p className="tn-choose__subtitle">How would you like to take part?</p>

      <div className="tn-choose__options">
        <button
          type="button"
          className="tn-choose__option"
          onClick={() => navigate(`/events/${eventId}/register`)}
        >
          <span className="tn-choose__option-title">{vocab.registerCta}</span>
        </button>

        <button
          type="button"
          className="tn-choose__option"
          onClick={() => navigate(`/events/${eventId}/sponsor`)}
        >
          <span className="tn-choose__option-title">Become a Sponsor</span>
        </button>
      </div>
    </div>
  );
}
